import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';

import type { LoaderKey } from '@common/enums';
import { LoadingService } from '@common/services';

import type { GrLoaderSize, GrLoaderTone, GrLoaderVariant } from './loader.types';

@Component({
  selector: 'gr-loader',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { style: 'display: contents;' },
  template: `
    @if (visible()) {
      <div
        [class]="rootClasses()"
        role="status"
        [attr.aria-live]="variant() === 'fullscreen' ? 'assertive' : 'polite'"
        [attr.aria-label]="ariaLabel() || resolvedLabel() || 'Loading'"
      >
        @if (variant() === 'fullscreen') {
          <div class="gr-loader-backdrop" aria-hidden="true"></div>
        }

        <div class="gr-loader-content inline-flex flex-col items-center justify-center gap-3">
          <span
            class="gr-loader-spinner relative inline-flex h-6 w-6 items-center justify-center"
            aria-hidden="true"
          >
            <span class="gr-loader-spinner-ring absolute inset-0"></span>
            <span class="gr-loader-spinner-ring gr-loader-spinner-ring--inner absolute"></span>
            @if (variant() === 'fullscreen') {
              <span class="gr-loader-spinner-core">
                <span class="material-icons-round" aria-hidden="true">hotel</span>
              </span>
            }
          </span>
          @if (resolvedLabel()) {
            <span class="gr-loader-label inline-flex items-baseline gap-1">
              {{ resolvedLabel()
              }}<span
                class="gr-loader-dots inline-block w-4 overflow-hidden align-baseline"
                aria-hidden="true"
              ></span>
            </span>
          }
        </div>
      </div>
    }
  `,
})
export class GrLoaderComponent {
  private readonly loadingService = inject(LoadingService);

  readonly variant = input<GrLoaderVariant>('inline');
  readonly size = input<GrLoaderSize>('md');
  readonly tone = input<GrLoaderTone>('brand');
  readonly label = input<string>('');
  readonly ariaLabel = input<string>('');
  readonly styleClass = input<string>('');
  readonly loaderKey = input<LoaderKey | null>(null);

  private readonly entry = computed(() => {
    const key = this.loaderKey();
    return key ? this.loadingService.entry(key)() : null;
  });

  protected readonly visible = computed(() => {
    if (!this.loaderKey()) return true;
    return this.entry() !== null;
  });

  protected readonly resolvedLabel = computed(() => this.label() || this.entry()?.label || '');

  protected readonly rootClasses = computed(() => {
    const classes = [
      'gr-loader',
      `gr-loader--${this.variant()}`,
      `gr-loader--${this.size()}`,
      `gr-loader--${this.tone()}`,
    ];
    if (this.variant() === 'inline') {
      classes.push('inline-flex', 'items-center', 'justify-center');
    } else if (this.variant() === 'block') {
      classes.push('flex', 'w-full', 'items-center', 'justify-center', 'px-4', 'py-8');
    } else {
      classes.push('fixed', 'inset-0', 'flex', 'items-center', 'justify-center');
    }
    const extra = this.styleClass();
    if (extra) classes.push(extra);
    return classes.join(' ');
  });
}
